import React, { useState, useEffect } from 'react';
import './Sidebar.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { FUNCTIONS_URL } from '../services/supabaseConfig';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  blink?: boolean;
}

interface MenuSection {
  header?: string; // If present, renders as a section header
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    items: [
      { id: 'home', label: 'Home', icon: '🏠', route: '/app' },
      { id: 'daily-reward', label: 'Daily Reward', icon: '🎁', route: '/daily-reward', blink: true },
      { id: 'finances', label: 'Finances', icon: '💰', route: '/finances' },
      { id: 'settings', label: 'Settings', icon: '⚙️', route: '/settings' },
    ],
  },
  {
    header: 'Traditional Leagues',
    items: [
      { id: 'traditional-leagues', label: 'Leagues', icon: '🏆', route: '/leagues' },
      { id: 'agent-office', label: 'My Players', icon: '⚾', route: '/players' },
      { id: 'owner-office', label: 'My Teams', icon: '🏟️', route: '/my-teams' },
      { id: 'forge', label: 'Forge', icon: '🔨', route: '/forge' },
      { id: 'marketplace', label: 'Marketplace', icon: '🏪', route: '/marketplace' },
      { id: 'free-agents', label: 'Free Agency', icon: '🆓', route: '/free-agents' },
    ],
  },
  {
    header: 'Quick Leagues',
    items: [
      { id: 'current-leagues', label: 'Leagues', icon: '⚡', route: '/quick-leagues' },
      { id: 'ql-lobby', label: 'Join a League', icon: '🎯', route: '/quick-league-lobby' },
      { id: 'ql-teams', label: 'My QL Teams', icon: '📋', route: '/ql-my-teams' },
      { id: 'team-builder', label: 'Team Builder', icon: '🏗️', route: '/team-builder' },
      { id: 'ql-top-owners', label: 'Top Owners', icon: '🏅', route: '/ql-top-owners' },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  userData?: any;
  isSignedIn?: boolean;
  onNavigate: (route: string) => void;
  activeItem?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, onNavigate, activeItem = 'home', userData, isSignedIn }) => {
  const [canClaimDaily, setCanClaimDaily] = useState<boolean | null>(null);

  useEffect(() => {
    if (isSignedIn && userData?.id) {
      checkDailyReward();
    } else {
      setCanClaimDaily(null);
    }
  }, [isSignedIn, userData?.id]);

  async function checkDailyReward() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setCanClaimDaily(false); return; }
      const res = await fetch(`${FUNCTIONS_URL}/daily-reward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'check-daily' }),
      });
      const data = await res.json();
      setCanClaimDaily(!data.claimed);
    } catch {
      setCanClaimDaily(null);
    }
  }

  return (
    <nav className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
      <div className="sidebar-menu">
        {menuSections.map((section, si) => (
          <div key={si} className="menu-section">
            {section.header && <span className="menu-section-label">{section.header}</span>}
            {section.items.map((item) => {
              const shouldBlink = item.blink && canClaimDaily === true;
              return (
                <button
                  key={item.id}
                  className={`menu-item ${activeItem === item.id ? 'active' : ''}`}
                  onClick={() => {
                    onNavigate(item.route);
                    onClose();
                  }}
                >
                  <span className={`menu-icon${shouldBlink ? ' blink-gold' : ''}`}>{item.icon}</span>
                  <span className="menu-label">{item.label}</span>
                  {shouldBlink && <span className="reward-dot" />}
                  {activeItem === item.id && <div className="active-indicator" />}
                </button>
              );
            })}
          </div>
        ))}
        
        <div className="menu-section" style={{ marginTop: 'auto' }}>
          {/* Docs section removed temporarily */}
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;
